import { createContext, useCallback, useContext, useMemo, useReducer } from "react";
import { toast } from "sonner";
import QuoteModal from "@/components/QuoteModal";
import CsvImportModal from "@/components/CsvImportModal";
import CartDrawer from "@/components/CartDrawer";
import { MIN_QUOTE_QUANTITY } from "@/lib/catalog";
import { useCart } from "./useCart";

const QuoteContext = createContext(null);
const dialogReducer = (state, patch) => ({ ...state, ...patch });

export function QuoteProvider({ children }) {
  const { cart, actions } = useCart();
  const [dialogs, dispatch] = useReducer(dialogReducer, { quote: false, csv: false, drawer: false, draft: null });
  const dialogActions = useMemo(() => ({
    openQuote: (prefill = null) => dispatch({ quote: true, draft: prefill }),
    close: () => dispatch({ quote: false, draft: null }),
    openCsvImport: () => dispatch({ csv: true }), closeCsvImport: () => dispatch({ csv: false }),
    openCartDrawer: () => { toast.dismiss("cart-added"); dispatch({ drawer: true }); },
    closeCartDrawer: () => dispatch({ drawer: false }),
  }), []);
  const totalQuantity = useMemo(() => cart.reduce((sum, item) => sum + Number(item.quantity || 1), 0), [cart]);
  const requestQuote = useCallback(() => {
    if (totalQuantity < MIN_QUOTE_QUANTITY) {
      const need = MIN_QUOTE_QUANTITY - totalQuantity;
      toast.error(`Add ${need} more door${need === 1 ? "" : "s"} to request a quote — our minimum is ${MIN_QUOTE_QUANTITY} doors.`);
      return;
    }
    dispatch({ drawer: false, draft: null, quote: true });
  }, [totalQuantity]);
  const state = useMemo(() => ({ cart, totalQuantity, meetsMinimum: totalQuantity >= MIN_QUOTE_QUANTITY,
    minQuantity: MIN_QUOTE_QUANTITY, drawerOpen: dialogs.drawer }), [cart, totalQuantity, dialogs.drawer]);
  const value = useMemo(() => ({ ...state, ...actions, ...dialogActions, requestQuote }), [state, actions, dialogActions, requestQuote]);
  return <QuoteContext.Provider value={value}>{children}
    <QuoteModal open={dialogs.quote} onClose={dialogActions.close} draftItem={dialogs.draft} onSubmitted={actions.clearCart} />
    <CsvImportModal open={dialogs.csv} onClose={dialogActions.closeCsvImport} />
    <CartDrawer open={dialogs.drawer} onClose={dialogActions.closeCartDrawer} />
  </QuoteContext.Provider>;
}

export function useQuote() {
  const value = useContext(QuoteContext);
  if (!value) throw new Error("useQuote must be used inside QuoteProvider");
  return value;
}